<template>
    <Card :title="$t('pages.AiProcessTaskEditPage')">
        <div class="card-body" v-if="aiProcessTask">
            <AppToast :title="aiProcessTask.product.name">
                <template #headerElements>
                    <AiProcessTaskStatusComponent :status="aiProcessTask.status" />
                </template>
                <div class="toast-body d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                        <ImageZoomComponent
                            :url="aiProcessTask.product.picture_url ?? PLACEHOLDER_IMAGE_URL"
                            :alt="aiProcessTask.product.name"
                        />
                        <div class="d-flex flex-column ml-2">
                            <span class="long-string">{{ aiProcessTask.product.name }}</span>
                            <span class="text-muted">{{ aiProcessTask.product.sku }}</span>
                        </div>
                    </div>
                    <router-link
                        :to="{ name: 'Product', params: { integrationProductUuid: aiProcessTask.product.uuid } }"
                        class="btn btn-link"
                    >
                        {{ $t('buttons.navigate') }}
                    </router-link>
                </div>
                <template #footer>
                    <div class="d-flex align-items-center">
                        <AppCheckbox :model-value="navigateToNext" @update:model-value="onNavigateToNextChange" />
                        <span class="ml-2">{{ $t('labels.ai_process_navigate_next') }}</span>
                    </div>
                    <div class="d-flex">
                        <AppButton color="light" class="mr-2" :disabled="!isActionable" @click="cancel"><i class="icon-trash mr-2"></i>{{ $t('buttons.cancel') }}</AppButton>
                        <AppButton color="indigo" :disabled="!isActionable" @click="confirm"><i class="icon-floppy-disk mr-2"></i>{{ $t('buttons.confirm') }}</AppButton>
                    </div>
                </template>
            </AppToast>

            <HorizontalTab :tabs="tabs" tab-key="key" tab-name="name" class="mt-3">
                <template #default="{ data }">
                    <div class="row mt-3">
                        <div class="col-lg-6">
                            <FormGroup :label="$t('labels.ai_process_old_version')">
                                <InputTextArea v-if="data.field === 'descriptions'" :model-value="oldValue(data) ?? ''" disabled />
                                <Tags v-else-if="data.field === 'keywords'" :model-value="oldValue(data) ?? ''" disabled />
                                <Input v-else :model-value="oldValue(data) ?? ''" disabled />
                            </FormGroup>
                        </div>
                        <div class="col-lg-6">
                            <FormGroup :label="$t('labels.ai_process_new_version')">
                                <InputTextArea v-if="data.field === 'descriptions'" :model-value="aiProcessTask[data.field]?.[data.locale] ?? ''" @update:model-value="val => aiProcessTask[data.field][data.locale] = val" />
                                <Tags v-else-if="data.field === 'keywords'" :model-value="aiProcessTask[data.field]?.[data.locale] ?? ''" @update:model-value="val => aiProcessTask[data.field][data.locale] = val" />
                                <Input v-else :model-value="aiProcessTask[data.field]?.[data.locale] ?? ''" @update:model-value="val => aiProcessTask[data.field][data.locale] = val" />
                            </FormGroup>
                        </div>
                    </div>
                </template>
            </HorizontalTab>
        </div>
    </Card>
</template>
<script>
import Card from "../../../../components/new/CardComponent.vue";
import AppToast from "../../../../components/new/AppToast.vue";
import AiProcessTaskStatusComponent from "../../../components/modules/integration/AiProcessTaskStatusComponent.vue";
import HorizontalTab from "../../../../components/new/tabs/HorizontalTab.vue";
import FormGroup from "../../../../components/new/form/group/FormGroup.vue";
import Input from "../../../../components/new/form/Input.vue";
import AppButton from "../../../../components/new/button/AppButton.vue";
import AppCheckbox from "../../../../components/new/form/checkbox/AppCheckbox.vue";
import InputTextArea from "../../../../components/new/form/InputTextArea.vue";
import Tags from "../../../../components/new/form/Tags.vue";
import ImageZoomComponent from "../../../../components/new/image/ImageZoomComponent.vue";
import { httpClient } from "../../../../services/HttpClient";
import MessageNotificator from "../../../../services/MessageNotificator";
import { PLACEHOLDER_IMAGE_URL } from "../../../../constants";
import { mapGetters } from "vuex";
import { getAiProcessFieldLabelKey, getAiProcessFields, getAiProcessTaskField } from "../../../enums/AiProcessFieldEnum";

const OLD_VERSION_FIELD_MAP = {
    names:        'names',
    descriptions: 'descriptions',
    keywords:     'seos',
};

export default {
    name: 'AiProcessTaskEditPage',
    components: { Card, AppToast, HorizontalTab, FormGroup, Input, InputTextArea, Tags, ImageZoomComponent, AppButton, AppCheckbox, AiProcessTaskStatusComponent },
    props: {
        integration: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            aiProcessTask: null,
            catalogProduct: null,
            navigateToNext: false,
            PLACEHOLDER_IMAGE_URL,
        };
    },
    watch: {
        '$route.params.aiProcessTaskUuid'() {
            this.loadData();
        },
    },
    computed: {
        ...mapGetters({
            project: 'route/project',
            profile: 'profile/profile',
        }),
        isActionable() {
            return ['success', 'confirmed', 'cancelled'].includes(this.aiProcessTask?.status);
        },
        tabs() {
            if (!this.aiProcessTask) return [];

            const tabs = [];
            for (const processField of getAiProcessFields(this.aiProcessTask.ai_process.settings)) {
                const field = getAiProcessTaskField(processField);
                const label = this.$t(getAiProcessFieldLabelKey(processField));

                for (const locale of this.integration.language_aliases) {
                    tabs.push({
                        key: `${field}_${locale}`,
                        name: `${label} (${this.$t(`languages.${locale}`)})`,
                        field,
                        locale,
                    });
                }
            }
            return tabs;
        },
    },
    methods: {
        oldValue(tab) {
            if (!this.catalogProduct) return '';
            const field = OLD_VERSION_FIELD_MAP[tab.field];
            return this.catalogProduct[field]?.[tab.locale] ?? '';
        },
        async cancel() {
            await httpClient.patch(
                `/projects/${this.$route.params.projectUuid}/pim/integrations/${this.integration.uuid}/ai-processes/${this.$route.params.aiProcessUuid}/tasks/${this.$route.params.aiProcessTaskUuid}`,
                {
                    status: 'cancelled',
                    names: this.aiProcessTask.names,
                    descriptions: this.aiProcessTask.descriptions,
                    keywords: this.aiProcessTask.keywords,
                }
            );

            new MessageNotificator().success(this.$t('alerts.success'));

            await this.navigateAfterAction();
        },
        async confirm() {
            await httpClient.patch(
                `/projects/${this.$route.params.projectUuid}/pim/integrations/${this.integration.uuid}/ai-processes/${this.$route.params.aiProcessUuid}/tasks/${this.$route.params.aiProcessTaskUuid}`,
                {
                    status: 'confirmed',
                    names: this.aiProcessTask.names,
                    descriptions: this.aiProcessTask.descriptions,
                    keywords: this.aiProcessTask.keywords,
                }
            );

            new MessageNotificator().success(this.$t('alerts.success'));

            await this.navigateAfterAction();
        },
        async navigateAfterAction() {
            if (this.navigateToNext) {
                const { data } = await httpClient.get(
                    `/projects/${this.$route.params.projectUuid}/pim/integrations/${this.integration.uuid}/ai-processes/${this.$route.params.aiProcessUuid}/tasks`,
                    { statuses: ['success'], limit: 1 }
                );

                const nextTask = data.data[0];

                if (nextTask) {
                    this.$router.push({
                        name: 'AiProcessTaskEditPage',
                        params: {
                            aiProcessUuid: this.$route.params.aiProcessUuid,
                            aiProcessTaskUuid: nextTask.uuid,
                        },
                    });

                    return;
                }
            }

            this.$router.push({
                name: 'AiProcessEditPage',
                params: { aiProcessUuid: this.$route.params.aiProcessUuid },
            });
        },
        async loadData() {
            this.aiProcessTask = null;
            this.catalogProduct = null;

            const processTaskResponse = await httpClient.get(
                `/projects/${this.$route.params.projectUuid}/pim/integrations/${this.integration.uuid}/ai-processes/${this.$route.params.aiProcessUuid}/tasks/${this.$route.params.aiProcessTaskUuid}`
            );
            this.aiProcessTask = processTaskResponse.data.data;
            this.navigateToNext = this.aiProcessTask.ai_process.navigate_to_next_product;

            const catalogProductResponse = await httpClient.get(
                `/integrations/products/${this.aiProcessTask.product.uuid}`
            );
            this.catalogProduct = catalogProductResponse.data.data;
        },
        onNavigateToNextChange(value) {
            this.navigateToNext = value;
            httpClient.put(
                `/projects/${this.$route.params.projectUuid}/pim/integrations/${this.integration.uuid}/ai-processes/${this.$route.params.aiProcessUuid}`,
                { navigate_to_next_product: value }
            ).then(() => {
                new MessageNotificator().success(this.$t('alerts.success'));
            });
        },
        onNotification(notification) {
            if (notification.type !== 'App\\Notifications\\Pim\\AiProcessTask\\AiProcessTaskStatusChangedNotification') {
                return;
            }

            const { ai_process_uuid, product_uuid, status } = notification.additional_data;

            if (ai_process_uuid !== this.$route.params.aiProcessUuid) {
                return;
            }

            if (product_uuid !== this.aiProcessTask?.product.uuid) {
                return;
            }

            this.aiProcessTask.status = status;
        },
    },
    async mounted() {
        await this.loadData();

        this.emitter.on('notification:received', this.onNotification);
    },
    beforeUnmount() {
        this.emitter.off('notification:received', this.onNotification);
    },
};
</script>
